import React, { useEffect, useState } from "react";
import { ASSETS, api, toEmbedUrl } from "../../lib/api";
import OrnamentalDivider from "./OrnamentalDivider";

export default function Gallery() {
    const [items, setItems] = useState([]);
    const [zoomed, setZoomed] = useState(null); // photo item currently zoomed

    useEffect(() => {
        api.get("/media?section=gallery")
            .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => setItems([]));
    }, []);

    useEffect(() => {
        if (!zoomed) return;
        const onKey = (e) => e.key === "Escape" && setZoomed(null);
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [zoomed]);

    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="section-pad bg-maroon-900 text-ivory relative overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(221,183,113,0.6) 1px, transparent 1px)",
                    backgroundSize: "4px 4px",
                }}
            />
            <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
                <div className="text-center mb-16">
                    <div className="eyebrow text-gold-300">· Legacy & Honours ·</div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory mt-4">
                        Moments that shaped the
                        <span className="italic text-gold-300"> Gurukula’s journey.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
                    <figure
                        data-testid="gallery-annualday"
                        onClick={() =>
                            setZoomed({
                                data_url: ASSETS.annualDay,
                                title: "Gurukula Annual Day · IGNCA",
                                caption:
                                    "Lighting the lamp with young Shishyas — commemorating the 103rd Jayanti of Bharata Ratna Pt. Bhimsen Joshi. Hosted at the Indira Gandhi National Centre for the Arts (IGNCA), Nagarabhavi.",
                            })
                        }
                        className="lg:col-span-12 group relative border border-gold/30 overflow-hidden cursor-zoom-in"
                    >
                        <img
                            src={ASSETS.annualDay}
                            alt="Shri Ananya Bhargava lighting the ceremonial lamp with young students before the Saraswati vigraha — Annual Day, hosted at Indira Gandhi National Centre for the Arts (IGNCA) commemorating the 103rd Jayanti of Bharata Ratna Pandit Bhimsen Joshi"
                            className="w-full h-[520px] object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-maroon-900 via-maroon-900/85 to-transparent">
                            <div className="eyebrow text-gold-300">· Gurukula Annual Day · IGNCA ·</div>
                            <h3 className="font-display text-2xl lg:text-3xl text-ivory mt-2 leading-tight max-w-4xl">
                                Lighting the lamp with young Shishyas — commemorating the
                                <span className="italic text-gold-300"> 103rd Jayanti of Bharata Ratna Pt. Bhimsen Joshi</span>.
                            </h3>
                            <p className="font-serif italic text-ivory/85 mt-2">
                                Hosted at the Indira Gandhi National Centre for the Arts (IGNCA), Nagarabhavi.
                            </p>
                        </figcaption>
                    </figure>

                    <figure
                        data-testid="gallery-vishnuvardhan"
                        onClick={() =>
                            setZoomed({
                                data_url: ASSETS.legacyVishnuvardhan,
                                title: "A Final Sangeeta Sadhana",
                                caption:
                                    "With Dr. Vishnuvardhan — the Kannada legend who learned music from Ananya in his final days.",
                            })
                        }
                        className="lg:col-span-7 group relative border border-gold/30 overflow-hidden cursor-zoom-in"
                    >
                        <img
                            src={ASSETS.legacyVishnuvardhan}
                            alt="Shri Ananya Bhargava with the late Dr. Vishnuvardhan"
                            className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 p-7 bg-gradient-to-t from-maroon-900 via-maroon-900/80 to-transparent">
                            <div className="eyebrow text-gold-300">A Final Sangeeta Sadhana</div>
                            <h3 className="font-display text-2xl text-ivory mt-2">
                                With Dr. Vishnuvardhan — the Kannada legend who learned music from Ananya in his final days.
                            </h3>
                        </figcaption>
                    </figure>

                    <figure
                        data-testid="gallery-award"
                        onClick={() =>
                            setZoomed({
                                data_url: ASSETS.legacyAward,
                                title: "State Recognition",
                                caption:
                                    "Honoured by Shri Siddaramaiah, Hon. Chief Minister of Karnataka.",
                            })
                        }
                        className="lg:col-span-5 group relative border border-gold/30 overflow-hidden cursor-zoom-in"
                    >
                        <img
                            src={ASSETS.legacyAward}
                            alt="Shri Ananya Bhargava receives an award from Karnataka CM Shri Siddaramaiah"
                            className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 p-7 bg-gradient-to-t from-maroon-900 via-maroon-900/80 to-transparent">
                            <div className="eyebrow text-gold-300">State Recognition</div>
                            <h3 className="font-display text-xl text-ivory mt-2">
                                Honoured by Shri Siddaramaiah, Hon. Chief Minister of Karnataka.
                            </h3>
                        </figcaption>
                    </figure>

                    {items.length > 0 && (
                        <div className="lg:col-span-12">
                            <div className="eyebrow text-gold-300 text-center mb-10 mt-4">
                                · From the Gurukula archives ·
                            </div>
                            <div
                                data-testid="gallery-admin-media"
                                className="grid grid-cols-1 md:grid-cols-2 gap-8"
                            >
                            {items.map((m, i) => (
                                <figure
                                    key={m.media_id}
                                    data-testid={`gallery-item-${m.media_id}`}
                                    className="relative group border border-gold/30 overflow-hidden bg-maroon/40"
                                >
                                    <span className="absolute -top-3 left-5 z-10 bg-gold-300 text-maroon-900 font-display text-sm px-3 py-1 tracking-widest">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {m.kind === "video" ? (
                                        <div className="relative aspect-video">
                                            <iframe
                                                title={m.title || "Gurukula video"}
                                                src={toEmbedUrl(m.url)}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                className="absolute inset-0 w-full h-full"
                                            />
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setZoomed(m)}
                                            data-testid={`gallery-zoom-${m.media_id}`}
                                            className="block w-full text-left cursor-zoom-in"
                                            aria-label={`Enlarge photo: ${m.title || m.caption || "Gurukula photo"}`}
                                        >
                                            <img
                                                src={m.data_url}
                                                alt={m.title || m.caption || "Gurukula photo"}
                                                className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                            />
                                        </button>
                                    )}
                                    {(m.title || m.caption) && (
                                        <figcaption className="p-5">
                                            {m.title && (
                                                <div className="font-display text-lg text-ivory leading-snug">
                                                    {m.title}
                                                </div>
                                            )}
                                            {m.caption && (
                                                <div className="font-serif italic text-ivory/75 text-sm mt-1 leading-relaxed">
                                                    {m.caption}
                                                </div>
                                            )}
                                        </figcaption>
                                    )}
                                </figure>
                            ))}
                            </div>
                        </div>
                    )}

                    <div
                        data-testid="gallery-quote"
                        className="lg:col-span-12 border border-gold/30 bg-maroon/40 p-10 lg:p-14 text-center"
                    >
                        <OrnamentalDivider className="mb-8" />
                        <p className="text-kannada text-2xl lg:text-3xl text-gold-300 leading-snug">
                            ಸಂಗೀತವು ಮನಸ್ಸನ್ನು ಶುದ್ಧೀಕರಿಸುತ್ತದೆ; ಮೌಲ್ಯಗಳು ಜೀವನವನ್ನು ಶುದ್ಧೀಕರಿಸುತ್ತವೆ.
                        </p>
                        <p className="font-display italic text-2xl lg:text-3xl text-ivory/95 mt-4">
                            “Music refines the mind, values refine life.”
                        </p>
                        <OrnamentalDivider className="mt-8" />
                    </div>
                </div>
            </div>

            {zoomed && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="gallery-lightbox"
                    onClick={() => setZoomed(null)}
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-maroon-900/95 backdrop-blur-sm p-6 cursor-zoom-out animate-fade-up"
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setZoomed(null);
                        }}
                        data-testid="gallery-lightbox-close"
                        aria-label="Close"
                        className="absolute top-5 right-5 text-ivory hover:text-gold-300 text-3xl font-display leading-none"
                    >
                        ×
                    </button>
                    <figure
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-6xl max-h-[90vh] flex flex-col items-center"
                    >
                        <img
                            src={zoomed.data_url}
                            alt={zoomed.title || zoomed.caption || "Gurukula photo enlarged"}
                            data-testid="gallery-lightbox-image"
                            className="max-h-[80vh] max-w-full object-contain shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
                        />
                        {(zoomed.title || zoomed.caption) && (
                            <figcaption className="mt-5 max-w-3xl text-center text-ivory">
                                {zoomed.title && (
                                    <div className="font-display italic text-2xl text-ivory">
                                        {zoomed.title}
                                    </div>
                                )}
                                {zoomed.caption && (
                                    <div className="font-serif text-ivory/80 mt-2 leading-relaxed">
                                        {zoomed.caption}
                                    </div>
                                )}
                            </figcaption>
                        )}
                    </figure>
                </div>
            )}
        </section>
    );
}
